// storageService.base.ts
// Shared implementation — imported by storageService.ts (native) and
// storageService.web.ts (web). Platform-specific encryption is handled
// one level down by secureStorage / secureStorage.web.ts.
import secureStorage from './secureStorage';

const AUTH_TOKEN_KEY = 'AUTH_TOKEN';
const REFRESH_TOKEN_KEY = 'REFRESH_TOKEN';
const LANGUAGE_KEY = 'LANGUAGE';
const COLOR_MODE_KEY = 'COLOR_MODE';
const LOGIN_HINT_KEY = 'LOGIN_HINT';
const FORCE_REAUTH_KEY = 'FORCE_REAUTH';
const AGENCY_CODE_KEY = 'AGENCY_CODE';

export const storageService = {
  /**
   * Retrieves the auth token from secure storage.
   * @returns A promise that resolves to the auth token string or null if not found.
   */
  async getAuthToken(): Promise<string | null> {
    return secureStorage.getItem(AUTH_TOKEN_KEY);
  },

  /**
   * Saves the auth token to secure storage.
   * @param token The auth token to save.
   */
  async setAuthToken(token: string): Promise<void> {
    await secureStorage.setItem(AUTH_TOKEN_KEY, token);
  },

  /**
   * Removes the auth token from secure storage.
   */
  async removeAuthToken(): Promise<void> {
    await secureStorage.removeItem(AUTH_TOKEN_KEY);
  },

  /**
   * Retrieves the refresh token from secure storage.
   * @returns A promise that resolves to the refresh token string or null if not found.
   */
  async getRefreshToken(): Promise<string | null> {
    return secureStorage.getItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Saves the refresh token to secure storage.
   * @param token The refresh token to save.
   */
  async setRefreshToken(token: string): Promise<void> {
    await secureStorage.setItem(REFRESH_TOKEN_KEY, token);
  },

  /**
   * Removes the refresh token from secure storage.
   */
  async removeRefreshToken(): Promise<void> {
    await secureStorage.removeItem(REFRESH_TOKEN_KEY);
  },

  /**
   * Saves the selected language to secure storage.
   * @param language The language code to save (e.g., 'en').
   */
  async setLanguage(language: string): Promise<void> {
    await secureStorage.setItem(LANGUAGE_KEY, language);
  },

  /**
   * Retrieves the saved language from secure storage.
   * @returns A promise that resolves to the language code string or null if not found.
   */
  async getLanguage(): Promise<string | null> {
    return await secureStorage.getItem(LANGUAGE_KEY);
  },

  /**
   * Saves the login hint (user email) for SSO account auto-selection.
   * @param hint The user's email or preferred_username from the ID token.
   */
  async setLoginHint(hint: string): Promise<void> {
    await secureStorage.setItem(LOGIN_HINT_KEY, hint);
  },

  /**
   * Retrieves the stored login hint.
   * @returns A promise that resolves to the login hint string or null if not found.
   */
  async getLoginHint(): Promise<string | null> {
    return secureStorage.getItem(LOGIN_HINT_KEY);
  },

  /**
   * Removes the stored login hint.
   */
  async removeLoginHint(): Promise<void> {
    await secureStorage.removeItem(LOGIN_HINT_KEY);
  },

  /**
   * Marks that the user explicitly logged out so the next sign-in
   * must force credential re-entry (prompt=login).
   */
  async setForceReauth(): Promise<void> {
    await secureStorage.setItem(FORCE_REAUTH_KEY, 'true');
  },

  /**
   * Returns true if a forced re-authentication is required (set after explicit logout).
   */
  async getForceReauth(): Promise<boolean> {
    return (await secureStorage.getItem(FORCE_REAUTH_KEY)) === 'true';
  },

  /**
   * Clears the force-reauth flag after a successful sign-in.
   */
  async clearForceReauth(): Promise<void> {
    await secureStorage.removeItem(FORCE_REAUTH_KEY);
  },

  /**
   * Persists the validated agency code so it survives page reloads
   * (e.g. after the OIDC redirect returns the user to ShopHomePage).
   */
  async setAgencyCode(code: string): Promise<void> {
    await secureStorage.setItem(AGENCY_CODE_KEY, code);
  },

  /**
   * Retrieves the persisted agency code.
   */
  async getAgencyCode(): Promise<string | null> {
    return secureStorage.getItem(AGENCY_CODE_KEY);
  },

  /**
   * Removes the persisted agency code (called on logout).
   */
  async removeAgencyCode(): Promise<void> {
    await secureStorage.removeItem(AGENCY_CODE_KEY);
  },

  /**
   * Persists the user's colour mode preference ('light' | 'dark' | 'system')
   * so it survives page reloads and app restarts.
   */
  async setColorMode(mode: string): Promise<void> {
    await secureStorage.setItem(COLOR_MODE_KEY, mode);
  },

  /**
   * Retrieves the persisted colour mode preference.
   * @returns A promise that resolves to the mode string or null if not set.
   */
  async getColorMode(): Promise<string | null> {
    return secureStorage.getItem(COLOR_MODE_KEY);
  },
};